import { Helmet } from 'react-helmet-async';

const DOMAIN = 'https://www.kingofit.ca';

// NOTE: Organization and LocalBusiness schemas with aggregateRating are defined ONCE
// in index.html to avoid "Review has multiple aggregate ratings" error.
// SEOHead only provides WebSite schema for SearchAction functionality.

// WebSite schema with SearchAction for AI assistants
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://www.kingofit.ca/#website",
  "name": "King of IT",
  "url": "https://www.kingofit.ca",
  "description": "Toronto IT Support & Managed Services Provider",
  "publisher": {
    "@id": "https://www.kingofit.ca/#organization"
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://www.kingofit.ca/blog?search={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
};

/**
 * Reusable SEO head component for per-route meta tags.
 * Every public page should render this with unique props.
 * Includes global Organization and WebSite schemas for AI assistants.
 */
const DEFAULT_KEYWORDS = 'IT support Toronto, computer repair Toronto, IT services GTA, managed IT services, cybersecurity Toronto, network setup, business IT support, computer help Toronto, tech support, IT consulting Toronto';

export default function SEOHead({
  title,
  description,
  keywords = DEFAULT_KEYWORDS,
  path = '/',
  ogImage = `${DOMAIN}/og-image.png`,
  ogType = 'website',
  noindex = false,
  additionalSchema = null,
}) {
  const canonicalUrl = `${DOMAIN}${path}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots - always set explicitly to override index.html default */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="King of IT" />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional meta for AI assistants */}
      <meta name="author" content="King of IT" />
      <meta name="geo.region" content="CA-ON" />
      <meta name="geo.placename" content="Toronto" />
      
      {/* WebSite Schema - helps AI understand site structure and enables search */}
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>

      {/* Additional page-specific schema if provided */}
      {additionalSchema && (
        <script type="application/ld+json">{JSON.stringify(additionalSchema)}</script>
      )}
    </Helmet>
  );
}

export { DOMAIN };
